/**
 * Closed domain vocabularies shared by the user dashboard. Most values are wire values that
 * the backend sends or expects byte for byte, so they must never be translated or restyled;
 * UI-only values are marked as such.
 */

/**
 * Template field types (`TemplateField.type`).
 * @readonly
 * @enum {string}
 */
export const FieldType = Object.freeze({
    TEXT: 'text',
    TEXTAREA: 'textarea',
    IMAGE: 'image',
    DOCUMENT: 'document',
    NUMBER: 'number',
    CHECKBOX: 'checkbox',
    TABLE: 'table',
    SELECT: 'select',
});

/**
 * Whether a field type is sent as an uploaded file (image or document).
 * @param {string | undefined} type
 * @returns {boolean}
 */
export function isFileFieldType(type) {
    return type === FieldType.IMAGE || type === FieldType.DOCUMENT;
}

/**
 * Whether a field type holds free text (single or multi line).
 * @param {string | undefined} type
 * @returns {boolean}
 */
export function isTextFieldType(type) {
    return type === FieldType.TEXT || type === FieldType.TEXTAREA;
}

/**
 * Column types of a `table` field (`TableColumn.type`).
 * @readonly
 * @enum {string}
 */
export const TableColumnType = Object.freeze({
    TEXT: 'text',
    NUMBER: 'number',
    SELECT: 'select',
    DOCUMENT: 'document',
});

/**
 * Template render modes. `INTERNAL` templates are layouts and partials that the dashboard
 * never offers to the user.
 * @readonly
 * @enum {string}
 */
export const RenderMode = Object.freeze({
    PAGE: 'PAGE',
    INDEX_FRAGMENT: 'INDEX_FRAGMENT',
    INTERNAL: 'INTERNAL',
});

/** Path prefix of pages rendered by `INDEX_FRAGMENT` templates. */
export const FRAGMENT_PATH_PREFIX = 'fragments/';

/** Path of the client's single homepage page: the site root file a homepage-flagged template publishes to. */
export const HOMEPAGE_PATH = 'index.html';

/**
 * Presentation of a layout context. `GRID` is a UI-only default: the wire leaves
 * `presentation` absent for grid contexts.
 * @readonly
 * @enum {string}
 */
export const LayoutPresentation = Object.freeze({
    GRID: 'grid',
    CAROUSEL: 'carousel',
});

/** Layout context whose grid fields use the bare base keys (`row`, not `homepage_row`). */
export const DEFAULT_LAYOUT_CONTEXT = 'homepage';

/**
 * Base keys of the grid placement fields of a layout context.
 * @readonly
 * @enum {string}
 */
export const GridKey = Object.freeze({
    ROW: 'row',
    COLUMN: 'column',
    WIDTH: 'width',
    HEIGHT: 'height',
});

/** Grid base keys in their canonical order. */
export const GRID_BASE_KEYS = Object.freeze([GridKey.ROW, GridKey.COLUMN, GridKey.WIDTH, GridKey.HEIGHT]);

/**
 * Whether a context is the default layout context (an empty context counts as the default).
 * @param {string | null | undefined} context
 * @returns {boolean}
 */
export function isDefaultLayoutContext(context) {
    return !context || context === DEFAULT_LAYOUT_CONTEXT;
}

/**
 * Field key of a grid value in a layout context: the bare base key for the default context,
 * `{context}_{base}` otherwise.
 * @param {string | null} context
 * @param {string} base One of {@link GRID_BASE_KEYS}
 * @returns {string}
 */
export function gridFieldKey(context, base) {
    return isDefaultLayoutContext(context) ? base : `${context}_${base}`;
}

/**
 * Most columns a grid context has: the column and width limit when the context fixes no column
 * count, and the largest column count it may fix.
 */
export const GRID_MAX_COLUMNS = 10;

/** Checkbox field that pins a page to its grid position. */
export const PIN_POSITION_KEY = 'pin_position';

/** Reserved page data key holding the section-composition state. */
export const COMPOSITION_DATA_KEY = 'wb_composition';

/** Reserved key of every table row, holding its stable row id. */
export const TABLE_ROW_ID_KEY = '_id';

/** Row ids the backend accepts. No `.`, so a row id never splits a `{field}.{row}.{column}` part name. */
export const TABLE_ROW_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

/**
 * Separator of an item reference `{sectionId}:{itemId}`. Section ids are slugs and item ids
 * never contain it, so it splits a reference unambiguously.
 */
export const ITEM_REF_SEPARATOR = ':';

/**
 * Focal points an image can be anchored to (`{key}_position`), in grid order. The empty string
 * (no focal point) is accepted as well.
 */
export const FOCAL_POINT_VALUES = Object.freeze([
    'left top', 'center top', 'right top',
    'left center', 'center center', 'right center',
    'left bottom', 'center bottom', 'right bottom',
]);

/**
 * Suffixes of the companion keys an image field carries.
 * @readonly
 * @enum {string}
 */
export const ImageCompanionSuffix = Object.freeze({
    ALT: '_alt',
    POSITION: '_position',
});

/**
 * Alt-text key of an image field.
 * @param {string} imageKey
 * @returns {string}
 */
export function altKey(imageKey) {
    return `${imageKey}${ImageCompanionSuffix.ALT}`;
}

/**
 * Focal-point key of an image field.
 * @param {string} imageKey
 * @returns {string}
 */
export function positionKey(imageKey) {
    return `${imageKey}${ImageCompanionSuffix.POSITION}`;
}

/**
 * Companion keys of an image field: alt text, then focal point.
 * @param {string} imageKey
 * @returns {string[]}
 */
export function imageCompanionKeys(imageKey) {
    return [altKey(imageKey), positionKey(imageKey)];
}

/**
 * Slot kinds of a composition layout diagram.
 * @readonly
 * @enum {string}
 */
export const DiagramSlot = Object.freeze({
    MEDIA: 'media',
    TEXT: 'text',
});

/**
 * Diagram kinds of a composition appearance or row band.
 * @readonly
 * @enum {string}
 */
export const AppearanceDiagram = Object.freeze({
    OUTLINE: 'outline',
    FILLED: 'filled',
    PLAIN: 'plain',
});

/**
 * The row grid of a composition: a block takes a span of `COLUMNS` columns, and at most
 * `MAX_BLOCKS_PER_ROW` blocks share a row. Mirrors the backend `CompositionRows`.
 * @readonly
 */
export const CompositionGrid = Object.freeze({
    COLUMNS: 12,
    MAX_BLOCKS_PER_ROW: 2,
});

/**
 * Operation of a staged page.
 * @readonly
 * @enum {string}
 */
export const StagedOperation = Object.freeze({
    CREATE: 'CREATE',
    EDIT: 'EDIT',
});

/**
 * Status of a publish job, as carried by a `snapshot` event.
 * @readonly
 * @enum {string}
 */
export const PublishJobStatus = Object.freeze({
    QUEUED: 'QUEUED',
    RUNNING: 'RUNNING',
    COMPLETED: 'COMPLETED',
    FAILED: 'FAILED',
});

/**
 * Where the preview of the site stands (`GET /site-preview`): none yet, being built, ready to
 * open at its address, or failed.
 * @readonly
 * @enum {string}
 */
export const SitePreviewStatus = Object.freeze({
    NONE: 'none',
    BUILDING: 'building',
    READY: 'ready',
    FAILED: 'failed',
});

/**
 * Stages of a publish job. The values double as the names of the publish stream's
 * server-sent events and as the page list's progress stages.
 * @readonly
 * @enum {string}
 */
export const PublishStage = Object.freeze({
    QUEUED: 'queued',
    RUNNING: 'running',
    COMPLETED: 'completed',
    FAILED: 'failed',
});

/** Server-sent event carrying a full publish job status. */
export const PUBLISH_SNAPSHOT_EVENT = 'snapshot';

/**
 * Sort orders of the page list.
 * @readonly
 * @enum {string}
 */
export const PageSort = Object.freeze({
    DATE_DESC: 'date-desc',
    DATE_ASC: 'date-asc',
    TITLE_ASC: 'title-asc',
    TITLE_DESC: 'title-desc',
    TEMPLATE: 'template',
});

/** Field keys the backend derives a page's URL from: the slug, falling back to the title. */
export const PageKey = Object.freeze({
    SLUG: 'slug',
    TITLE: 'title',
});

/** URL and search-engine fields grouped under the page settings heading. */
export const SETTING_FIELD_KEYS = Object.freeze([PageKey.SLUG, 'meta_description', 'meta_title', 'seo_title', 'seo_description']);

/** Text spellings of a checked flag, compared after trimming and lower-casing. */
const TRUTHY_FLAG_VALUES = new Set(['true', '1', 'yes', 'on']);

/**
 * Whether a stored or typed flag value means "checked": `true`, or one of `true`, `1`, `yes`
 * and `on` in any case and surrounded by any whitespace.
 * @param {unknown} value
 * @returns {boolean}
 */
export function isTruthyFlag(value) {
    return value === true || TRUTHY_FLAG_VALUES.has(String(value ?? '').trim().toLowerCase());
}

/**
 * Kinds of toast the notification component shows (UI-only).
 * @readonly
 * @enum {string}
 */
export const NotificationType = Object.freeze({
    SUCCESS: 'success',
    ERROR: 'error',
    WARNING: 'warning',
});

/**
 * Content types of the image formats the backend converts.
 * @readonly
 * @enum {string}
 */
export const ImageType = Object.freeze({
    JPEG: 'image/jpeg',
    PNG: 'image/png',
    GIF: 'image/gif',
    WEBP: 'image/webp',
});

/** Image content types the backend accepts for an uploaded image. */
export const UPLOAD_IMAGE_TYPES = Object.freeze(Object.values(ImageType));

/** Largest file the backend accepts per upload, in bytes (10 MB). */
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

/** Widest image the backend publishes, in pixels; mirrors backend `ImageOptimizationService.MAX_WIDTH`. */
export const MAX_IMAGE_WIDTH = 2400;

/** Tallest image the backend publishes, in pixels; mirrors backend `ImageOptimizationService.MAX_HEIGHT`. */
export const MAX_IMAGE_HEIGHT = 1600;

/** Response header that names the kind of a failed page request (`ErrorCode`). */
export const ERROR_CODE_HEADER = 'X-Error-Code';

/**
 * Kinds of page-publication failure the backend names in the `X-Error-Code` header: the
 * simple name of its error record. Only the codes the dashboard describes in its own words
 * are listed; any other code falls back to the HTTP status.
 * @readonly
 * @enum {string}
 */
export const ErrorCode = Object.freeze({
    NO_CHANGES: 'NoChanges',
    NO_DRAFTS: 'NoDrafts',
    HOMEPAGE_NOT_DELETABLE: 'HomepageNotDeletable',
    ASSET_UPLOAD_FAILURE: 'AssetUploadFailure',
    PATH_TAKEN: 'PathTaken',
});
